import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector, useAppDispatch } from '@/store';
import { performSessionLogout, SESSION_TOAST, tryRefreshSession } from '@/utils/sessionManager';

const IDLE_TIMEOUT_MS = 55 * 60 * 1000;
const WARN_AT_MS = 50 * 60 * 1000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Inactivity-based session expiry with a visible warning and stay-signed-in action.
 */
export function useSessionExpiry(): void {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShownRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (expireTimerRef.current) { clearTimeout(expireTimerRef.current); expireTimerRef.current = null; }
  }, []);

  const startTimers = useCallback(() => {
    clearTimers();

    warnTimerRef.current = setTimeout(() => {
      warningShownRef.current = true;
      toast.warn(
        ({ closeToast }) => (
          <div className="text-sm leading-snug">
            <div className="font-semibold">Still there?</div>
            <div className="mt-1 text-white/90">
              You will be signed out in 5 minutes due to inactivity.
            </div>
            <button
              type="button"
              className="mt-2 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold hover:bg-white/25"
              onClick={() => {
                closeToast?.();
                warningShownRef.current = false;
                toast.dismiss(SESSION_TOAST.idleWarn);
                void tryRefreshSession(dispatch).finally(() => startTimers());
              }}
            >
              Stay signed in
            </button>
          </div>
        ),
        { toastId: SESSION_TOAST.idleWarn, autoClose: false, closeOnClick: false },
      );
    }, WARN_AT_MS);

    expireTimerRef.current = setTimeout(() => {
      void performSessionLogout(dispatch, 'idle');
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, dispatch]);

  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;

    if (warningShownRef.current) {
      toast.dismiss(SESSION_TOAST.idleWarn);
      warningShownRef.current = false;
    }

    startTimers();
  }, [isAuthenticated, startTimers]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      toast.dismiss(SESSION_TOAST.idleWarn);
      return undefined;
    }

    startTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [isAuthenticated, startTimers, handleActivity, clearTimers]);
}
